import { useState, useEffect, useRef } from "react";
import transparentBg from "../assets/images/transparent-bg.png";
import { X, ImageUp, EyeOff, Eye, SquarePen, Trash2 } from "lucide-react";
import { ClinicDetailsType } from "../types/doctor";
import toast from "react-hot-toast";
import { ClinicService } from "../services/api/clinic.service";
import { DoctorService } from "../services/api/doctor.service";
import { Clinic } from "../types/clinic";
import LocationPicker from "./GoogleMap";

interface AddNewClinicModalProps {
  onClose: () => void;
  onSuccess: () => void;
  initialData: Clinic | null;
}

interface AddressDetails {
  fullAddress: string;
  city: string;
  state: string;
  pincode: string;
}

export function AddNewClinicModal({
  onClose,
  onSuccess,
  initialData,
}: AddNewClinicModalProps) {
  const [isDragging, setIsDragging] = useState(false);
  const dropZoneRef = useRef<HTMLLabelElement>(null);
  const [address, setAddress] = useState("");
  const [location, setLocation] = useState<{ lat: number; lng: number } | null>(
    null,
  );
  const [clinicDetails, setClinicDetails] = useState<ClinicDetailsType>({
    id: "",
    name: "",
    mobileNumber: "",
    registrationNumber: "",
    drugLicenseNumber: "",
    address: "",
    profileImage: "",
    city: "",
    pincode: "",
    state: "",
    username: "",
    password: "",
    supervisorName: "",
    lat: "",
    long: "",
    fssaiNumber: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showMap, setShowMap] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<
    (() => void) | null
  >(null);

  // Google Map Link State
  const [googleMapLink, setGoogleMapLink] = useState("");
  const [fetchingLocation, setFetchingLocation] = useState(false);

  useEffect(() => {
    if (initialData) {
      console.log("Initial Data:", initialData);
      setClinicDetails({
        id: initialData?.id,
        name: initialData.name || "",
        mobileNumber: initialData.mobileNumber || "",
        registrationNumber: initialData.registrationNumber || "",
        drugLicenseNumber: initialData.drugLicenseNumber || "",
        address: initialData.address || "",
        profileImage: initialData.profileImage || "",
        username: initialData.username || "",
        password: initialData.password || "",
        city: initialData.city || "",
        pincode: initialData.pincode || "",
        state: initialData.state || "",
        supervisorName: initialData.supervisorName || "",
        lat: initialData.lat || "",
        long: initialData.long || "",
        fssaiNumber: initialData.fssaiNumber || "",
      });
    }
  }, [initialData]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setClinicDetails((prev) => ({ ...prev, profileImage: file }));
    }
  };

  useEffect(() => {
    setAddress(
      clinicDetails.address +
      " " +
      clinicDetails.city +
      " " +
      clinicDetails.state +
      "  " +
      clinicDetails.pincode,
    );
  }, [
    clinicDetails.address,
    clinicDetails.city,
    clinicDetails.state,
    clinicDetails.pincode,
  ]);

  const handleLocate = async () => {
    setShowMap(true);
  };

  const validateForm = (): string | true => {
    if (clinicDetails.name.trim().length === 0) {
      return "Clinic Name is Required";
    }
    if (clinicDetails.mobileNumber.trim().length === 0) {
      return "Mobile Number is Required";
    }
    // if (!clinicDetails.mobileNumber.match(/^\d{10}$/)) {
    //   return "Invalid Mobile Number";
    // }
    if (clinicDetails.registrationNumber.trim().length === 0) {
      return "Clinic Registration Number is Required";
    }
    if (clinicDetails.drugLicenseNumber.trim().length === 0) {
      return "Drug License Number is Required";
    }
    if (clinicDetails.address.trim().length === 0) {
      return "Address is required for registration";
    }
    if (clinicDetails.state.trim().length === 0) {
      return "State is required for registration";
    }
    if (clinicDetails.city.trim().length === 0) {
      return "City is required for registration";
    }
    if (clinicDetails.pincode.trim().length === 0) {
      return "Pincode is required for registration";
    }
    if (clinicDetails.pincode.length < 6) {
      return "Invalid Pincode";
    }
    if (clinicDetails.supervisorName.trim().length === 0) {
      return "Supervisor is Required";
    }
    return true;
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const validationResult = validateForm();

    if (validationResult != true) {
      toast.error(validationResult);
      return;
    }
    const formData = new FormData();
    formData.append("name", clinicDetails.name);
    formData.append("mobileNumber", clinicDetails.mobileNumber);
    formData.append("address", clinicDetails.address);
    formData.append("pincode", clinicDetails.pincode);
    formData.append("username", initialData ? clinicDetails.username : `${clinicDetails.mobileNumber}_clinic`);
    formData.append("password", initialData ? clinicDetails.password : `${clinicDetails.mobileNumber}@Clinic`);
    formData.append("state", clinicDetails.state);
    formData.append("city", clinicDetails.city);
    formData.append("profileImage", clinicDetails.profileImage);
    formData.append("registrationNumber", clinicDetails.registrationNumber);
    formData.append("drugLicenseNumber", clinicDetails.drugLicenseNumber);
    formData.append("supervisorName", clinicDetails.supervisorName);
    formData.append("lat", clinicDetails.lat);
    formData.append("long", clinicDetails.long);
    formData.append("role", "Clinic");
    if (clinicDetails.fssaiNumber) {
      formData.append("fssaiNumber", clinicDetails.fssaiNumber);
    }
    try {
      if (initialData) {
        // Edit: Update existing Clinic
        const response = await ClinicService.updateExistingClinic(
          initialData.id,
          formData,
        );
        if (response.success) {
          toast.success(response.message);
          onSuccess();
        }
      } else {
        const response = await ClinicService.addNewClinic(formData);
        if (response.success) {
          toast.success(response.message);
          onSuccess();
        } else {
          toast.error(response.message);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong. Please try again.");
    }
  };

  const handleDeletePhoto = async (imgType: string) => {
    const clinicId = clinicDetails?.id;

    if (!clinicId) {
      console.error("Error: clinicId is undefined.");
      alert("Clinic ID is missing. Please try again later.");
      return;
    }

    console.log("Attempting to delete image for clinicId:", clinicId);

    try {
      const response = await ClinicService.DeleteClinicImage(clinicId);

      if (response.success) {
        setClinicDetails((prev) => ({
          ...prev,
          [imgType]: "",
        }));
      } else {
        console.error("Failed to delete image:", response.message);
        alert("Failed to delete image. Please try again.");
      }
    } catch (error) {
      console.error("Error deleting image:", error);
      alert("Something went wrong.");
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "copy";
    setIsDragging(true);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFiles = Array.from(e.dataTransfer.files).filter((file) =>
        file.type.startsWith("image/"),
      );
      if (droppedFiles.length > 0) {
        setClinicDetails({ ...clinicDetails, profileImage: droppedFiles[0] });
      } else {
        toast.error("Please drop only image files");
      }
      // Clear the dataTransfer object
      e.dataTransfer.clearData();
    }
  };

  const handleFetchLocation = async () => {
    if (!googleMapLink) {
      toast.error("Please enter a Google Maps link");
      return;
    }

    setFetchingLocation(true);
    try {
      const response = await DoctorService.getMapDetails(googleMapLink);

      if (response.success && response.data) {
        const { lat, long, address, city, state, pincode } = response.data;

        setClinicDetails((prev) => ({
          ...prev,
          lat: lat,
          long: long,
          address: address || prev.address,
          city: city || prev.city,
          state: state || prev.state,
          pincode: pincode || prev.pincode,
        }));

        toast.success("Location details fetched successfully!");
      } else {
        toast.error(response.message || "Failed to fetch location details");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      toast.error("An error occurred while fetching location details");
    } finally {
      setFetchingLocation(false);
    }
  };

  // Custom Confirmation Modal
  const ExitConfirmationModal = ({
    onConfirm,
    onCancel,
  }: {
    onConfirm: () => void;
    onCancel: () => void;
  }) => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[400px] transform transition-all scale-100">
        <div className="flex flex-col items-center text-center">
          <div className="bg-orange-100 p-3 rounded-full mb-4">
            <h1 className="text-3xl">⚠️</h1>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Unsaved Changes
          </h3>
          <p className="text-gray-500 mb-6">
            You have unsaved changes. Are you sure you want to leave? All your
            progress will be lost.
          </p>
          <div className="flex gap-3 w-full">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors shadow-sm"
            >
              Leave
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const checkUnsavedChanges = () => {
    if (initialData) {
      // Simple check for edit mode
      // Note: This is a basic comparison. For more robust check, we'd compare all fields.
      return (
        clinicDetails.name !== (initialData.name || "") ||
        clinicDetails.mobileNumber !== (initialData.mobileNumber || "") ||
        clinicDetails.address !== (initialData.address || "")
      );
    }
    // Add mode
    return (
      clinicDetails.name.trim() !== "" ||
      clinicDetails.mobileNumber.trim() !== "" ||
      clinicDetails.registrationNumber.trim() !== "" ||
      clinicDetails.address.trim() !== ""
    );
  };

  const handleConfirmExit = () => {
    setShowExitConfirmation(false);
    if (pendingNavigation) {
      pendingNavigation();
      setPendingNavigation(null);
    } else {
      onClose();
    }
  };

  const handleCancelExit = () => {
    setShowExitConfirmation(false);
    setPendingNavigation(null);
    window.history.pushState(null, document.title, window.location.href);
  };

  const handleClose = () => {
    if (checkUnsavedChanges()) {
      setShowExitConfirmation(true);
      setPendingNavigation(() => onClose);
    } else {
      onClose();
    }
  };

  useEffect(() => {
    window.history.pushState(null, document.title, window.location.href);

    const handlePopState = (event: PopStateEvent) => {
      if (checkUnsavedChanges()) {
        window.history.pushState(null, document.title, window.location.href);
        setShowExitConfirmation(true);
        setPendingNavigation(() => onClose);
      } else {
        onClose();
      }
    };

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (checkUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = "";
        return "";
      }
    };

    window.addEventListener("popstate", handlePopState);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [clinicDetails, initialData]);

  const handleLocationSelect = (
    lat: string,
    lng: string,
    addressDetails: AddressDetails,
  ) => {
    setClinicDetails((prev) => ({
      ...prev,
      lat,
      long: lng,
      address: addressDetails.fullAddress,
      city: addressDetails.city,
      state: addressDetails.state,
      pincode: addressDetails.pincode,
    }));
  };

  return (
    <section className="absolute inset-0 z-50" style={{ height: "129vh" }}>
      <div
        className="absolute inset-0 bg-cover bg-center flex  justify-center px-2 pb-2 overflow-hidden overflow-y-auto"
        style={{
          backgroundImage: `url(${transparentBg})`,
          //   height: "inherit",
        }}
      >
        {showExitConfirmation && (
          <ExitConfirmationModal
            onConfirm={handleConfirmExit}
            onCancel={handleCancelExit}
          />
        )}
        <div className="bg-white rounded-xl w-[650px] shadow-xl mt-5 py-4 px-6 h-fit">
          {/* Modal Header */}
          <div className="flex items-center justify-between border-b p-4">
            <h2 className="text-lg font-medium text-emerald-600">
              {initialData ? "UPDATE CLINIC" : "ADD CLINIC"}
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {/* Modal Header */}

          <form className="w-full p-1">
            {/* Profile picture upload and showcase */}
            <div
              className={`flex items-center my-2 ${clinicDetails?.profileImage ? "justify-start" : "justify-center"
                }`}
            >
              {clinicDetails.profileImage ? (
                <>
                  <div className="w-24 h-24 overflow-hidden rounded-full border border-gray-300">
                    <img
                      src={
                        clinicDetails.profileImage instanceof File
                          ? URL.createObjectURL(clinicDetails.profileImage)
                          : clinicDetails.profileImage
                      }
                      alt="Patient"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <label
                    htmlFor="clinic-image"
                    className="cursor-pointer text-sm outline-none font-md text-[#647588]"
                  >
                    <input
                      type="file"
                      id="clinic-image"
                      name="profileImage"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                    <SquarePen className="w-5 h-5 ml-3" />
                  </label>
                  <button
                    onClick={() => handleDeletePhoto("profileImage")}
                    type="button"
                    className="text-sm outline-none font-md text-[#FF6C6C]"
                  >
                    <Trash2 className="w-5 h-5 ml-3" />
                  </button>
                </>
              ) : (
                <label
                  ref={dropZoneRef}
                  htmlFor="clinic-image"
                  className={`cursor-pointer rounded-md flex items-center flex-col justify-center bg-gray-100 py-2 ${isDragging
                    ? "border-2 border-dashed border-emerald-500 bg-emerald-50"
                    : ""
                    }`}
                  style={{
                    width: clinicDetails?.profileImage
                      ? "calc(100% - 120px)"
                      : "calc(100%)",
                  }}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    id="clinic-image"
                    accept="image/*"
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                  <label
                    htmlFor="clinic-image"
                    className="cursor-pointer w-11 h-11 mx-auto mb-2 rounded-full bg-emerald-600 flex items-center justify-center"
                  >
                    <ImageUp className="w-5 h-5 text-white" />
                  </label>
                  <p className="text-xs text-gray-500">
                    Click to upload or drag and drop your photo
                  </p>
                  <p className="text-xs text-gray-500">
                    Upload photo (optional)
                  </p>
                </label>
              )}
            </div>

            {/* Profile picture upload and showcase */}

            {/* Clinic Name and mobile number */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="clinicName"
                  className="block text-sm text-gray-600 font-medium"
                >
                  Clinic Name
                </label>
                <input
                  id="clinicName"
                  name="clinicName"
                  type="text"
                  value={clinicDetails.name}
                  onChange={(e) =>
                    setClinicDetails((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter Clinic Name"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="mobileNumber"
                  className="block text-sm text-gray-600 font-medium"
                >
                  Contact Number
                </label>
                <input
                  id="mobileNumber"
                  name="mobileNumber"
                  type="tel"
                  value={clinicDetails.mobileNumber}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d{0,10}$/.test(value)) {
                      setClinicDetails((prev) => ({
                        ...prev,
                        mobileNumber: value,
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter mobile number"
                />
              </div>
            </div>
            {/* Clinic and mobile number */}

            {/* registrationNumber and drugLicenseNumber */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="registrationNumber"
                  className="block text-sm text-gray-600 font-medium"
                >
                  Clinic Registration Number
                </label>
                <input
                  id="registrationNumber"
                  name="registrationNumber"
                  type="text"
                  value={clinicDetails.registrationNumber}
                  onChange={(e) =>
                    setClinicDetails((prev) => ({
                      ...prev,
                      registrationNumber: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder=" Clinic Registration Number"
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="drugLicenseNumber"
                  className="block text-sm text-gray-600 font-medium"
                >
                  Drug License Number
                </label>
                <input
                  id="drugLicenseNumber"
                  name="drugLicenseNumber"
                  type="text"
                  value={clinicDetails.drugLicenseNumber}
                  onChange={(e) =>
                    setClinicDetails((prev) => ({
                      ...prev,
                      drugLicenseNumber: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter DRUG License Number"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="fssaiNumber"
                  className="block text-sm text-gray-600 font-medium"
                >
                  FSSAI Number
                </label>
                <input
                  id="fssaiNumber"
                  name="fssaiNumber"
                  type="text"
                  value={clinicDetails.fssaiNumber}
                  onChange={(e) =>
                    setClinicDetails((prev) => ({
                      ...prev,
                      fssaiNumber: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter FSSAI Number"
                />
              </div>
            </div>
            {/* registrationNumber and drugLicenseNumber */}

            {/* Google Map Link */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 font-medium mb-2">
                Google Map Link
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={googleMapLink}
                  onChange={(e) => setGoogleMapLink(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Paste Google Maps Link (e.g., https://maps.app.goo.gl/...)"
                />
                <button
                  type="button"
                  onClick={handleFetchLocation}
                  disabled={fetchingLocation}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 whitespace-nowrap"
                >
                  {fetchingLocation ? "Fetching..." : "Fetch Location"}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Paste a Google Maps link and click "Fetch Location" to auto-fill
                details.
              </p>
            </div>

            {/* City,Pincode and State */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="State"
                  className="block text-sm text-gray-600 font-medium"
                >
                  State
                </label>
                <input
                  id="state"
                  name="state"
                  type="text"
                  value={clinicDetails.state}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      setClinicDetails((prev) => ({
                        ...prev,
                        state: value,
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter State"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="city"
                  className="block text-sm text-gray-600 font-medium"
                >
                  City
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={clinicDetails.city}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^[A-Za-z\s]*$/.test(value)) {
                      setClinicDetails((prev) => ({
                        ...prev,
                        city: value,
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter City"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="pincode"
                  className="block text-sm text-gray-600 font-medium"
                >
                  Pincode
                </label>
                <input
                  id="pincode"
                  name="pincode"
                  type="text"
                  value={clinicDetails.pincode}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value.length <= 6 && /^\d*$/.test(value)) {
                      setClinicDetails((prev) => ({
                        ...prev,
                        pincode: value,
                      }));
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter Pincode"
                />
              </div>
            </div>
            {/* City,pincode and state */}

            {/* Address */}
            <div className="space-y-2 mb-2">
              <label className="block text-sm text-gray-600 font-medium">
                Address
              </label>
              <textarea
                value={clinicDetails.address}
                onChange={(e) =>
                  setClinicDetails((prev) => ({
                    ...prev,
                    address: e.target.value,
                  }))
                }
                className="resize-none w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                placeholder="Enter Address"
              />
            </div>
            <button
              type="button"
              onClick={handleLocate}
              className="px-4 py-2 my-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              🗺️ Locate on Map
            </button>
            {/* Address */}

            {/* supervisorName */}
            <div className="grid grid-cols-1 gap-4 mb-4">
              <div className="space-y-2">
                <label
                  htmlFor="clinicName"
                  className="block text-sm text-gray-600 font-medium"
                >
                  Supervisor Name
                </label>
                <input
                  id="supervisorName"
                  name="supervisorName"
                  type="text"
                  value={clinicDetails.supervisorName}
                  onChange={(e) =>
                    setClinicDetails((prev) => ({
                      ...prev,
                      supervisorName: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 outline-none rounded-lg focus:ring-1 focus:ring-emerald-500"
                  placeholder="Enter supervisor Name"
                />
              </div>
            </div>
            {/* supervisorName */}



            {/* Buttons Box */}
            <div className="flex items-center flex-row-reverse justify-start gap-5 mt-5">
              <button
                onClick={handleSubmit}
                className="w-[120px]  px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:bg-gray-200 disabled:cursor-not-allowed"
              >
                {initialData ? "Update" : "Save"}
              </button>

              <button
                type="button"
                onClick={handleClose}
                className="w-[120px] px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
        {showMap && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
            <LocationPicker
              location={location}
              setLocation={setLocation}
              address={address}
              setShowMap={setShowMap}
              onLocationSelect={handleLocationSelect}
            />
          </div>
        )}
      </div>
    </section>
  );
}
